import { as_array, form_questions_from_sections, merge_assigned_status, object_entries, object_values, progress_width_class, safe_get_key, safe_section_access_key, safe_set, workflow_status_insight } from './configure_workflow_helpers';

export default {
    workflow_name() {
      return String(this.$route?.query?.workflowName || '').trim() || 'Workflow';
    },

    workflow_id() {
      const workflow_id = Number(this.$route?.query?.workflowId || 0);
      return Number.isFinite(workflow_id) && workflow_id > 0 ? workflow_id : null;
    },

    route_project_id() {
      const project_id = Number(this.$route?.query?.project_id || this.$route?.query?.projectId || 0);
      return Number.isFinite(project_id) && project_id > 0 ? project_id : null;
    },

    assigned_statuses() {
      return this.assigned_status_configs
        .map((assignment) => merge_assigned_status(this.all_statuses, assignment))
        .filter(Boolean)
        .sort((first, second) => Number(first.order || 0) - Number(second.order || 0));
    },

    selected_form() {
      return this.workflow_forms.find((form) => String(form.id) === String(this.selected_form_id)) || null;
    },

    selected_form_label() {
      return this.selected_form?.name
        ? this.selected_form.name
        : 'No form selected';
    },

    workflow_summary_form_label() {
      return this.selected_form_label;
    },

    selected_status_label() {
      return this.selected_status?.name
        ? this.selected_status.name
        : 'No status selected';
    },

    workflow_summary_status_id() {
      return this.selected_status?.id || this.assigned_statuses[0]?.id || '';
    },

    workflow_summary_status_label() {
      const active_status = this.assigned_statuses.find((status) => String(status.id) === String(this.workflow_summary_status_id));
      return active_status?.name || 'No status selected';
    },

    selected_section_access_assignment_label() {
      return this.selected_section_access_assignment?.label
        ? this.selected_section_access_assignment.label
        : 'No reviewer selected';
    },

    unassigned_workflow_forms() {
      const assigned_ids = new Set(this.workflow_forms.map((form) => Number(form.id)));

      return this.available_forms.filter((form) => !assigned_ids.has(Number(form.id)));
    },

    selected_form_assigned_status_map() {
      return new Map(
        (this.assigned_form_statuses || []).map((status) => [
          Number(status?.status_id || status?.id || 0),
          status,
        ]).filter(([status_id]) => status_id > 0)
      );
    },

    selected_form_statuses() {
      const workflow_status_map = new Map(
        this.assigned_statuses.map((status) => [String(status.id), status])
      );

      return this.selected_form_status_ids
        .map((status_id, index, ids) => {
          const status = workflow_status_map.get(String(status_id));
          const assignment = this.selected_form_assigned_status_map.get(Number(status_id)) || {};
          if (!status) return null;

          return {
            ...status,
            isInitial: index === 0,
            isFinal: index === ids.length - 1,
            persistedInitial: Boolean(assignment?.is_initial),
            persistedFinal: Boolean(assignment?.is_final),
          };
        })
        .filter(Boolean);
    },

    displayed_assigned_statuses() {
      if (!this.selected_form_status_ids.length) {
        return this.assigned_statuses;
      }

      const selected_ids = this.selected_form_status_ids.map((status_id) => String(status_id));
      const selected_id_set = new Set(selected_ids);
      const selected_statuses = selected_ids
        .map((status_id) => this.assigned_statuses.find((status) => String(status.id) === status_id))
        .filter(Boolean);
      const unselected_statuses = this.assigned_statuses.filter(
        (status) => !selected_id_set.has(String(status.id))
      );

      return [...selected_statuses, ...unselected_statuses];
    },

    assigned_form_status_lookup() {
      return new Map(
        (this.assigned_form_statuses || []).map((status) => [
          Number(status?.id || 0),
          {
            id: Number(status?.id || 0),
            statusId: String(status?.status_id || ''),
            name: String(status?.status_name || ''),
            colorCode: this.all_statuses.find((item) => item.id === String(status?.status_id || ''))?.colorCode || '#6c757d',
          },
        ]).filter(([status_id]) => status_id > 0)
      );
    },

    transition_form_statuses() {
      if (!this.selected_form_id) return [];
      
      return (this.assigned_form_statuses || [])
        .map(status => ({
          assignedFormStatusId: Number(status.id),
          workflowStatusId: Number(status.status_id),
          name: status.status_name,
          order: status.order,
          isInitial: Boolean(status.is_initial),
          isFinal: Boolean(status.is_final),
        }))
        .filter(status => status.assignedFormStatusId > 0)
        .sort((a, b) => a.order - b.order);
    },

    workflow_assigned_status_ids() {
      return new Set(
        this.assigned_status_configs.map((assignment) => String(assignment.status_id || ''))
      );
    },

    available_statuses() {
      return this.all_statuses;
    },

    filtered_available_statuses() {
      const query = String(this.assignable_status_query || '').trim().toLowerCase();
      if (!query) {
        return this.available_statuses;
      }

      return this.available_statuses.filter((status) =>
        String(status?.name || '').toLowerCase().includes(query)
      );
    },

    selected_assignable_statuses() {
      const selected_ids = new Set(
        (Array.isArray(this.selected_assignable_status_ids) ? this.selected_assignable_status_ids : []).map((status_id) => String(status_id))
      );

      return this.available_statuses.filter(
        (status) => selected_ids.has(String(status.id)) && !this.workflow_assigned_status_ids.has(String(status.id))
      );
    },

    selected_assignable_status_summary() {
      if (!this.selected_assignable_statuses.length) {
        return 'Select one or more statuses';
      }

      if (this.selected_assignable_statuses.length <= 2) {
        return this.selected_assignable_statuses.map((status) => status.name).join(', ');
      }

      const [first_status, second_status] = this.selected_assignable_statuses;
      return `${first_status?.name || ''}, ${second_status?.name || ''} +${this.selected_assignable_statuses.length - 2} more`;
    },

    all_statuses_assigned() {
      return Boolean(this.all_statuses.length) && this.assigned_statuses.length >= this.all_statuses.length;
    },

    selected_status() {
      return this.assigned_statuses.find((status) => String(status.id) === String(this.selected_status_id)) || null;
    },

    visible_transitions() {
      const active_assigned_status_id = Number(this.selected_form_transition_status?.assignedFormStatusId || 0);
      return !active_assigned_status_id
        ? []
        : this.transitions.filter(
          (transition) =>
            Number(transition.sourceId) === active_assigned_status_id
            || Number(transition.targetId) === active_assigned_status_id
        );
    },

    total_nodes_label() {
      const unique_nodes = new Set();

      this.visible_transitions.forEach((transition) => {
        unique_nodes.add(transition.source);
        unique_nodes.add(transition.target);
      });

      return String(unique_nodes.size).padStart(2, '0');
    },

    total_paths_label() {
      return String(this.visible_transitions.length).padStart(2, '0');
    },

    logic_health_label() {
      return this.transitions.length ? '100%' : '0%';
    },

    selected_transition_type() {
      return this.transition_types.find(
        (item) => String(item.id) === String(this.transition_builder?.trigger?.transition_type_id || this.selected_transition_type_id)
      ) || null;
    },

    all_form_questions() {
      return form_questions_from_sections(this.form_sections);
    },

    transition_condition_rules() {
      return Array.isArray(this.transition_builder?.conditions?.rules)
        ? this.transition_builder.conditions.rules
        : [];
    },

    transition_condition_operator() {
      return this.transition_builder?.conditions?.operator || 'AND';
    },

    transition_condition_mode() {
      return this.transition_builder?.conditions?.mode || 'none';
    },

    transition_section_options() {
      const options = [];

      (this.form_sections || []).forEach((section) => {
        const main_id = Number(section.id || section.section_id || section.main_section_id || 0);
        const main_name = String(
          section.section_name
          || section.name
          || section.main_section_name
          || `Section ${main_id || ''}`
        ).trim();

        if (main_id > 0) {
          options.push({ id: main_id, name: main_name });
        }

        const nested_sections = Array.isArray(section.sub_sections)
          ? section.sub_sections
          : (Array.isArray(section.sections) ? section.sections : []);
        if (nested_sections.length) {
          nested_sections.forEach((sub_section) => {
            const sub_id = Number(sub_section.id || sub_section.section_id || sub_section.sub_section_id || 0);
            const sub_name = String(
              sub_section.section_name
              || sub_section.name
              || `Section ${sub_id || ''}`
            ).trim();

            if (sub_id > 0) {
              options.push({ id: sub_id, name: sub_name });
            }
          });
        }
      });

      return options.filter((section, index, arr) =>
        section.id > 0 && arr.findIndex((item) => item.id === section.id) === index
      );
    },

    transition_role_options() {
      return [
        { id: '', name: 'No role required' },
        ...this.reviewer_role_options.map((role) => ({
          id: String(role.id),
          name: role.name,
        })),
      ];
    },

    transition_status_options() {
      return this.transition_form_statuses.map((status) => ({
        id: String(status.assignedFormStatusId),
        name: status.name,
      }));
    },

    transition_list() {
      return (this.transitions || []).map(rule => ({
        id: rule.id,
        form: rule.form,
        source: rule.source,
        target: rule.target,
        trigger: rule.actionTrigger,
        type: rule.transitionType,
        conditions: rule.conditionsSummary || 'No conditions',
        fullRule: rule
      }));
    },

    transition_source_status() {
      const id = this.transition_builder.movement.from_status_id;
      return this.transition_form_statuses.find(s => String(s.assignedFormStatusId) === String(id)) || null;
    },

    transition_target_status() {
      const id = this.transition_builder.movement.to_status_id;
      return this.transition_form_statuses.find(s => String(s.assignedFormStatusId) === String(id)) || null;
    },

    transition_trigger_action() {
      const id = this.transition_builder.trigger.action_id;
      return this.workflow_actions.find(a => String(a.id) === String(id)) || null;
    },

    is_automatic_transition_type() {
      const transition_type_name = String(this.selected_transition_type?.name || '').trim().toLowerCase();
      return transition_type_name === 'automatic' || transition_type_name.includes('automatic');
    },

    transition_type_helper_text() {
      return 'Manual = user clicks button. Automatic = system moves form automatically.';
    },

    transition_condition_helper_text() {
      if (this.is_automatic_transition_type) {
        return 'Automatic transitions must use advanced conditions so the system knows when to move the form.';
      }
      return 'Conditions decide whether the form can move to the next status.';
    },

    transition_rule_preview_sentence() {
      return this.build_transition_preview_sentence({
        source: this.transition_source_status?.name || '[Source]',
        target: this.transition_target_status?.name || '[Target]',
        action: this.transition_trigger_action?.name || '[Action]',
        transitionType: this.selected_transition_type?.name || '',
        condition: {
          rules: this.transition_condition_rules,
        },
      });
    },

    selected_form_transition_label() {
      return this.selected_form?.name
        ? this.selected_form.name
        : 'the selected form';
    },

    transition_builder_is_valid() {
      const transition_builder = this.transition_builder || {};
      const movement = transition_builder.movement || {};
      const trigger = transition_builder.trigger || {};
      return Boolean(
        movement.form_id
        && movement.from_status_id
        && movement.to_status_id
        && trigger.transition_type_id
        && (this.is_automatic_transition_type || trigger.action_id)
        && (!this.is_automatic_transition_type || this.transition_condition_mode === 'advanced')
      );
    },

    transition_builder_validation_message() {
      const transition_builder = this.transition_builder || {};
      const movement = transition_builder.movement || {};
      const trigger = transition_builder.trigger || {};
      if (!movement.form_id) return 'Select a workflow form.';
      if (!movement.from_status_id) return 'Select a source status.';
      if (!movement.to_status_id) return 'Select a target status.';
      if (!trigger.transition_type_id) return 'Select a transition type.';
      if (!this.is_automatic_transition_type && !trigger.action_id) return 'Select a trigger action for a manual transition.';
      if (this.is_automatic_transition_type && this.transition_condition_mode !== 'advanced') return 'Use advanced conditions for automatic transitions.';
      return '';
    },

    selected_form_transition_status() {
      return this.transition_form_statuses.find(
        (status) => String(status.workflowStatusId) === String(this.selected_status_id)
      ) || null;
    },

    selected_form_status_assignment_id() {
      return Number(this.selected_form_transition_status?.assignedFormStatusId || 0);
    },

    transition_rule_heading() {
      const from_name = this.transition_source_status?.name || this.selected_form_transition_status?.name || this.selected_status?.name || 'Status';
      const to_name = this.transition_target_status?.name || 'Target status';
      return `Transition Rule: ${from_name} -> ${to_name}`;
    },

    workflow_setup_steps() {
      return [
        { id: 'statuses', label: '1. Assign statuses', description: 'Open the status library and add the workflow stages in order.', complete: this.assigned_statuses.length > 0, active: !this.assigned_statuses.length },
        { id: 'form', label: '2. Choose a form', description: 'Pick the form that this workflow should control.', complete: Boolean(this.selected_form), active: this.assigned_statuses.length > 0 && !this.selected_form },
        { id: 'transitions', label: '3. Connect transitions', description: 'Select a status card and define how records move to the next status.', complete: this.transitions.length > 0, active: Boolean(this.selected_form) && this.assigned_statuses.length > 0 && !this.transitions.length },
        { id: 'reviewers', label: '4. Assign reviewers', description: 'For each important status, choose who can review and approve it.', complete: !this.reviewers_required_for_any_status || Boolean(this.status_reviewers?.users?.length || this.status_reviewers?.roles?.length), active: Boolean(this.selected_form && this.selected_status) },
      ];
    },

    workflow_tabs() {
      const has_selected_status_context = Boolean(this.selected_form && this.selected_status);
      const has_manual_status_context = has_selected_status_context && this.selected_status_has_manual_transitions;
      const has_section_access_scope = has_manual_status_context && this.assigned_reviewers.length > 0;

      return [
        { id: 'forms', label: 'Forms', caption: 'Assign the forms that should run on this workflow.', enabled: true },
        { id: 'statuses', label: 'Statuses', caption: 'Add stages and choose the workflow form.', enabled: true },
        { id: 'transitions', label: 'Transitions', caption: 'Connect assigned statuses into a usable flow.', enabled: has_selected_status_context },
        { id: 'reviewers', label: 'Reviewers', caption: 'Decide who can review each selected status.', enabled: has_manual_status_context },
        { id: 'section_access', label: 'Section Access', caption: 'Control which workflow sections are visible and editable.', enabled: has_section_access_scope },
        { id: 'actions', label: 'Actions', caption: 'Manage workflow actions, automations, and triggers.', enabled: Boolean(this.selected_form) },
        { id: 'preview', label: 'Preview', caption: 'Visualize the final workflow configuration.', enabled: Boolean(this.assigned_statuses.length) },
      ];
    },

    workflow_step_tabs() {
      return this.workflow_tabs.map((tab, index) => ({
        ...tab,
        step: index + 1,
        short_label: `${index + 1}. ${tab.label}`,
      }));
    },

    active_workflow_tab_config() {
      return this.workflow_tabs.find((tab) => tab.id === this.active_workflow_tab) || this.workflow_tabs[0];
    },

    workflow_health_progress_class() {
      return progress_width_class(this.workflow_health_state === 'Ready' ? 100 : 50);
    },

    workflow_health_state() {
      if (this.workflow_health_error_count) return 'Incomplete';
      if (this.workflow_health_warning_count) return 'Warning';
      return this.transitions.length && this.assigned_statuses.length && this.workflow_forms.length ? 'Ready' : 'Incomplete';
    },

    workflow_health_text_class() {
      if (this.workflow_health_state === 'Ready') return 'text-success';
      if (this.workflow_health_state === 'Warning') return 'text-warning';
      return 'text-danger';
    },

    workflow_health_detail() {
      if (this.workflow_health_error_count) {
        return `${this.workflow_health_error_count} setup item${this.workflow_health_error_count === 1 ? '' : 's'} must be completed.`;
      }
      if (this.workflow_health_warning_count) {
        return `${this.workflow_health_warning_count} warning${this.workflow_health_warning_count === 1 ? '' : 's'} should be reviewed.`;
      }
      return 'Workflow is ready for activation.';
    },

    workflow_health_error_count() {
      let count = 0;
      if (!this.workflow_forms.length) count += 1;
      if (!this.assigned_statuses.length) count += 1;
      if (!this.transitions.length) count += 1;
      count += (this.transitions || []).filter((transition) => !transition.target || Number(transition.targetId || 0) <= 0).length;
      count += (this.transitions || []).filter((transition) => !this.is_automatic_transition_record(transition) && !String(transition.actionTrigger || transition.action || '').trim()).length;
      return count;
    },

    workflow_health_warning_count() {
      let count = 0;
      count += this.workflow_unreachable_status_count;
      count += this.workflow_status_insights.filter((status) => status.noOutgoingWarning).length;
      if (this.workflow_manual_transition_count && !this.assigned_reviewers.length) count += 1;
      if (this.assigned_reviewers.length && !this.workflow_section_access_count) count += 1;
      count += (this.transitions || []).filter((transition) => this.is_automatic_transition_record(transition) && String(transition.conditionsSummary || '').trim().toLowerCase() === 'no conditions').length;
      return count;
    },

    assigned_forms_progress_class() {
      return progress_width_class(Math.min(this.workflow_forms.length * 20, 100));
    },

    configured_transitions_progress_class() {
      return progress_width_class(Math.min(this.transitions.length * 15, 100));
    },

    workflow_entry_status_label() {
      const form_initial = this.selected_form_statuses.find((status) => status.isInitial || status.is_initial || status.persistedInitial);
      const transition_initial = this.transition_form_statuses.find((status) => status.isInitial || status.is_initial || status.persistedInitial);
      const explicit_initial = this.assigned_statuses.find((status) => status.isInitial || status.is_initial || status.persistedInitial);
      const no_incoming = this.workflow_status_insights.find((status) => !status.incoming && status.outgoing);
      return form_initial?.name || transition_initial?.name || explicit_initial?.name || no_incoming?.name || this.assigned_statuses[0]?.name || 'Not set';
    },

    workflow_preview_assigned_form_label() {
      const names = this.workflow_forms
        .map((form) => String(form.name || form.form_name || '').trim())
        .filter(Boolean);
      if (!names.length) return 'No form assigned';
      if (names.length <= 2) return names.join(', ');
      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
    },

    workflow_preview_type_label() {
      if (this.workflow_manual_transition_count && this.workflow_automatic_transition_count) return 'Hybrid Workflow';
      if (this.workflow_automatic_transition_count) return 'Automatic Workflow';
      if (this.workflow_manual_transition_count) return 'Manual Workflow';
      return 'Not configured';
    },

    workflow_manual_transition_count() {
      return (this.transitions || []).filter((transition) => !this.is_automatic_transition_record(transition)).length;
    },

    workflow_automatic_transition_count() {
      return (this.transitions || []).filter((transition) => this.is_automatic_transition_record(transition)).length;
    },

    workflow_condition_transition_count() {
      return (this.transitions || []).filter((transition) => {
        const summary = String(transition.conditionsSummary || '').trim().toLowerCase();
        return summary && summary !== 'no conditions';
      }).length;
    },

    workflow_terminal_status_count() {
      return this.workflow_status_insights.filter((status) => status.terminal).length;
    },

    workflow_unreachable_status_count() {
      return this.workflow_status_insights.filter((status) => status.unreachable).length;
    },

    workflow_status_insights() {
      const transitions = as_array(this.transitions);
      return as_array(this.assigned_statuses).map((status, index) =>
        workflow_status_insight(status, index, transitions, this.workflow_status_name_is_terminal)
      );
    },

    workflow_section_access_count() {
      return object_values(this.section_access_matrix).reduce((total, action_ids) => (
        total + (Array.isArray(action_ids) ? action_ids.length : 0)
      ), 0);
    },

    workflow_next_step_message() {
      if (!this.workflow_forms.length) {
        return 'Assign a form to this workflow.';
      }
      if (!this.assigned_statuses.length) {
        return 'Add statuses so the workflow has a path.';
      }
      if (this.assigned_statuses.length && this.selected_form && !this.transitions.length) {
        return `Create transitions from ${this.workflow_entry_status_label}.`;
      }
      if (this.workflow_manual_transition_count && !this.assigned_reviewers.length) {
        const status = this.transitions.find((transition) => !this.is_automatic_transition_record(transition))?.source || 'manual transition status';
        return `Assign reviewers for ${status}.`;
      }
      if (this.assigned_reviewers.length && !this.workflow_section_access_count) {
        return `Configure section permissions for ${this.workflow_summary_reviewer_scope}.`;
      }
      if (this.workflow_health_state === 'Ready') {
        return 'Workflow is ready for activation.';
      }

      if (!this.reviewers_required_for_any_status) {
        return 'The workflow is now configurable. Automatic transitions do not require reviewer setup.';
      }

      return 'The workflow is now configurable. Review transitions and reviewer assignments for each status.';
    },

    section_access_role_options() {
      return (this.status_reviewers?.roles || []).map((role) => ({
        key: `role:${role.id}`,
        type: 'role',
        assignment_id: Number(role.id),
        label: `${role.role_name} (Role)`,
      }));
    },

    section_access_user_options() {
      return (this.status_reviewers?.users || []).map((user) => ({
        key: `user:${user.id}`,
        type: 'user',
        assignment_id: Number(user.id),
        label: `${user.name} (User)`,
      }));
    },

    section_access_assignment_options() {
      return [...this.section_access_role_options, ...this.section_access_user_options];
    },

    selected_section_access_assignment() {
      if (this.selected_section_access_user_key) {
        return this.section_access_user_options.find(
          (option) => option.key === this.selected_section_access_user_key
        ) || null;
      }
      if (this.selected_section_access_role_key) {
        return this.section_access_role_options.find(
          (option) => option.key === this.selected_section_access_role_key
        ) || null;
      }

      return null;
    },

    selected_section_access_assignment_key() {
      return this.selected_section_access_assignment?.key || '';
    },

    current_reviewer_scope_label() {
      return this.selected_section_access_assignment_label;
    },

    section_access_context_sentence() {
      return `${this.current_reviewer_scope_label} can perform the selected actions on ${this.selected_form_label} while status is ${this.selected_status_label}.`;
    },

    section_access_matrix_rows() {
      const rows = [];

      (this.form_sections || []).forEach((main_section, main_index) => {
        const main_section_name = String(
          main_section?.main_section_name
          || main_section?.section_name
          || main_section?.name
          || `Main Section ${main_index + 1}`
        ).trim();

        const nested_sections = Array.isArray(main_section?.sub_sections)
          ? main_section.sub_sections
          : (Array.isArray(main_section?.sections) ? main_section.sections : []);

        if (nested_sections.length) {
          nested_sections
            .slice()
            .sort((a, b) => Number(a?.order_number || 0) - Number(b?.order_number || 0))
            .forEach((section, section_index) => {
            const section_id = Number(section?.section_id || section?.id || section?.sub_section_id || 0);
            if (section_id <= 0) return;
            rows.push({
              formSectionId: section_id,
              sectionName: String(section?.section_name || section?.name || `Section ${section_index + 1}`).trim(),
              mainSectionName: main_section_name,
              description: String(section?.description || main_section?.description || '').trim(),
            });
            });
          return;
        }

        const section_id = Number(main_section?.id || main_section?.section_id || main_section?.main_section_id || 0);
        if (section_id <= 0) return;
        rows.push({
          formSectionId: section_id,
          sectionName: main_section_name,
          mainSectionName: main_section_name,
          description: String(main_section?.description || '').trim(),
        });
      });

      return rows;
    },

    section_access_state() {
      return object_entries(this.section_access_matrix).reduce((acc, [section_id, action_ids]) => {
        (Array.isArray(action_ids) ? action_ids : []).forEach((action_id) => {
          safe_set(acc, safe_section_access_key(section_id, action_id), true);
        });
        return acc;
      }, {});
    },

    section_access_save_disabled() {
      return !this.selected_section_access_assignment || this.section_access_loading || this.section_access_saving;
    },

    can_manage_section_access() {
      return Boolean(
        this.selected_form?.id
        && this.selected_status?.id
        && this.section_access_assignment_options.length
        && this.form_sections.length
        && this.workflow_actions.length
      );
    },

    selected_reviewer_user_records() {
      const selected_ids = new Set(this.selected_reviewer_user_ids.map(Number));
      return (this.status_reviewers?.users || []).filter((item) => selected_ids.has(Number(item.user_id)));
    },

    reviewer_user_options() {
      const query = String(this.reviewer_user_query || '').trim().toLowerCase();
      const users = Array.isArray(this.available_users) ? this.available_users : [];

      if (!query) {
        return users;
      }

      return users.filter((user) => {
        const name = String(user?.name || '').toLowerCase();
        const email = String(user?.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    },

    assigned_reviewers() {
      const approval_order_map = this.approval_rule_order_by_reviewer_user_id || {};
      const saved_user_reviewers = (this.status_reviewers?.users || []).map((user) => ({
        key: `user:${user.id}`,
        id: Number(user.id),
        reviewerType: 'user',
        userId: Number(user.user_id || user.id),
        label: String(user.name || user.user_name || 'User').trim(),
        description: String(user.email || 'Specific user reviewer').trim(),
        workflowReviewerId: Number(user.id),
        order: safe_get_key(approval_order_map, String(user.id)) || null,
      }));
      const saved_role_reviewers = (this.status_reviewers?.roles || []).map((role) => ({
        key: `role:${role.id}`,
        id: Number(role.id),
        reviewerType: 'role',
        roleId: Number(role.role_id || role.id),
        label: String(role.role_name || role.name || 'Role').trim(),
        description: 'Role-based reviewer assignment',
        workflowReviewerId: Number(role.id),
        order: null,
      }));

      const saved_user_ids = new Set(saved_user_reviewers.map((reviewer) => Number(reviewer.userId)));
      const saved_role_ids = new Set(saved_role_reviewers.map((reviewer) => Number(reviewer.roleId)));

      const pending_user_reviewers = this.selected_reviewer_user_ids
        .map((user_id) => Number(user_id))
        .filter((user_id) => user_id > 0 && !saved_user_ids.has(user_id))
        .map((user_id) => ({
          key: `pending-user:${user_id}`,
          id: user_id,
          reviewerType: 'user',
          userId: user_id,
          label: this.selected_reviewer_user_label(user_id),
          description: 'Pending user reviewer',
          workflowReviewerId: user_id,
          order: null,
        }));

      const pending_role_reviewers = this.selected_reviewer_role_ids
        .map((role_id) => Number(role_id))
        .filter((role_id) => role_id > 0 && !saved_role_ids.has(role_id))
        .map((role_id) => ({
          key: `pending-role:${role_id}`,
          id: role_id,
          reviewerType: 'role',
          roleId: role_id,
          label: this.selected_reviewer_role_label(role_id),
          description: 'Pending role reviewer',
          workflowReviewerId: role_id,
          order: null,
        }));

      return [
        ...saved_user_reviewers,
        ...saved_role_reviewers,
        ...pending_user_reviewers,
        ...pending_role_reviewers,
      ];
    },

    workflow_summary_reviewer_scope() {
      const reviewer = this.assigned_reviewers[0];
      if (!reviewer) {
        return 'No reviewer selected';
      }

      return reviewer.reviewerType === 'role'
        ? `${reviewer.label} (Role)`
        : `${reviewer.label} (User)`;
    },

    reviewer_user_assignments() {
      return this.assigned_reviewers
        .filter((reviewer) => reviewer.reviewerType === 'user')
        .map((reviewer, index) => ({
          ...reviewer,
          order: Number(reviewer.order || index + 1),
        }));
    },

    manual_transition_source_status_ids() {
      return (this.transitions || [])
        .filter((transition) => {
          const transition_type = String(transition.transitionType || transition.type || '').trim().toLowerCase();
          return transition_type && transition_type !== 'automatic' && !transition_type.includes('automatic');
        })
        .map((transition) => Number(transition.sourceId || 0))
        .filter((status_id) => status_id > 0);
    },

    selected_status_transition_rules() {
      const selected_assigned_status_id = Number(this.selected_form_transition_status?.assignedFormStatusId || 0);
      if (!selected_assigned_status_id) {
        return [];
      }

      return (this.transitions || []).filter(
        (transition) => Number(transition.sourceId || 0) === selected_assigned_status_id
      );
    },

    selected_context_transition() {
      return this.selected_status_transition_rules.find(
        (transition) => String(transition.id) === String(this.selected_context_transition_id)
      ) || null;
    },

    selected_context_transition_label() {
      if (!this.selected_context_transition) return 'No transition selected';
      return `${this.selected_context_transition.source} -> ${this.selected_context_transition.target}`;
    },

    selected_status_has_manual_transitions() {
      return this.selected_status_transition_rules.some((transition) => {
        const transition_type = String(transition.transitionType || transition.type || '').trim().toLowerCase();
        return transition_type && transition_type !== 'automatic' && !transition_type.includes('automatic');
      });
    },

    selected_status_has_automatic_transitions() {
      return this.selected_status_transition_rules.some((transition) => {
        const transition_type = String(transition.transitionType || transition.type || '').trim().toLowerCase();
        return transition_type === 'automatic' || transition_type.includes('automatic');
      });
    },

    selected_status_requires_reviewers() {
      return this.selected_status_has_manual_transitions;
    },

    selected_status_is_automatic_only() {
      return this.selected_status_has_automatic_transitions && !this.selected_status_has_manual_transitions;
    },

    reviewers_required_for_any_status() {
      return this.manual_transition_source_status_ids.length > 0;
    },

    reviewer_count_warning() {
      if (!this.selected_status_requires_reviewers || !this.assigned_reviewers.length) {
        return '';
      }

      return this.assigned_reviewers.length < Number(this.reviewer_count_required || 1)
        ? `At least ${this.reviewer_count_required} reviewer(s) are required for this status.`
        : '';
    },

    reviewer_scope_text() {
      const form = this.selected_form?.name || 'No form selected';
      const status = this.selected_status?.name || 'No status selected';

      if (this.selected_status_is_automatic_only) {
        return `Automatic transitions on ${form} while in ${status} are handled by the system when conditions are met.`;
      }

      if (this.selected_status_requires_reviewers) {
        return `Manual transitions on ${form} while in ${status} require reviewers to approve movement between statuses.`;
      }

      return `These reviewers only apply to ${form} when it is in ${status} status.`;
    },

    reviewer_form_options() {
      return this.workflow_forms;
    },

    reviewer_status_options() {
      const required_status_ids = new Set(this.manual_transition_source_status_ids.map(String));
      const options = this.transition_form_statuses
        .filter((status) => required_status_ids.has(String(status.assignedFormStatusId)))
        .map((status) => ({
          id: String(status.workflowStatusId),
          name: status.name,
        }));

      if (this.selected_status?.id && !options.some((status) => String(status.id) === String(this.selected_status.id))) {
        options.unshift({
          id: String(this.selected_status.id),
          name: this.selected_status.name,
        });
      }

      return options;
    },

    review_assignment_disabled() {
      return !this.selected_form_id || !this.selected_status_id;
    },

    has_approval_transitions() {
      return this.selected_status_requires_reviewers;
    },

    reviewers_empty_state_title() {
      if (this.selected_status_is_automatic_only) {
        return 'No reviewer setup required.';
      }

      return `No reviewers assigned for ${this.selected_form_label} while in ${this.selected_status_label} status.`;
    },

    reviewers_empty_state_message() {
      if (this.selected_status_is_automatic_only) {
        return 'This status currently only contains automatic transitions.';
      }

      return 'Add at least one role or user reviewer to support approval-based transitions and workflow logic for this status.';
    },

    section_access_optional_message() {
      return 'Section access is optional because this status uses automatic transitions.';
    },

    transition_save_guidance_message() {
      if (this.is_automatic_transition_type) {
        return 'This automatic transition does not require reviewers.';
      }

      return 'Next step: Assign reviewers for this transition.';
    },

    action_usage_by_id() {
      return (this.transitions || []).reduce((acc, transition) => {
        const action_id = Number(transition.actionTriggerId || 0);
        if (action_id > 0) {
          const key = String(action_id);
          const current_count = Number(safe_get_key(acc, key) || 0);
          safe_set(acc, key, current_count + 1);
        }
        return acc;
      }, {});
    },
};
